import { Dispatch, SetStateAction, useState } from 'react';
import * as yup from 'yup';
import { GuidePaymentMethod, GuideTypeForm } from 'types/guide';

export type GuideValidation = {
  value_discount?: string;
  value_marker?: string;
  value_procedure?: string;
  patient_id?: string;
  procedure_id?: string;
  doctor_id?: string;
  appointment_date?: string;
  marker_id?: string;
  payment_method_id?: string;
};

type UseGuideValidation = [
  GuideValidation,
  Dispatch<SetStateAction<GuideValidation>>,
  (guide: GuideTypeForm, paymentMethod: GuidePaymentMethod) => Promise<void>,
];

export function useGuideValidation(): UseGuideValidation {
  async function handleValidation(guide: GuideTypeForm, paymentMethod: GuidePaymentMethod) {
    const schema = yup.object().shape({
      value_procedure: yup
        .string()
        .required('O valor do procedimento é obrigatório')
        .test('is-positive', 'O valor do procedimento deve ser maior que zero', value => {
          if (!value) return false;
          const numberValue = Number(value.replace(',', '.'));
          return numberValue > 0;
        }),
      value_discount: yup
        .string()
        .test('is-not-negative', 'O valor do desconto não pode ser negativo', value => {
          if (!value) return true;
          const numberValue = Number(value.replace(',', '.'));
          return numberValue >= 0;
        })
        .test(
          'is-less-than-procedure',
          'O valor do desconto não pode ser maior que o valor do procedimento',
          function (value) {
            if (!value) return true;
            const discountValue = Number(value.replace(',', '.'));
            const procedureValue = Number(this.parent.value_procedure?.replace(',', '.') || 0);
            return discountValue <= procedureValue;
          },
        ),
      value_marker: yup
        .string()
        .test('is-not-negative', 'O valor do marcador não pode ser negativo', value => {
          if (!value) return true;
          const numberValue = Number(value.replace(',', '.'));
          return numberValue >= 0;
        })
        .test(
          'is-less-than-procedure',
          'O valor do marcador não pode ser maior que o valor do procedimento',
          function (value) {
            if (!value) return true;
            const markerValue = Number(value.replace(',', '.'));
            const procedureValue = Number(this.parent.value_procedure?.replace(',', '.') || 0);
            return markerValue <= procedureValue;
          },
        ),
      payment_method_id: yup
        .number()
        .moreThan(0, 'O método de pagamento é obrigatório')
        .required('O método de pagamento é obrigatório'),
      marker_id: yup.number().required('O marcador é obrigatório'),
      doctor_id: yup.number().required('O médico é obrigatório'),
      procedure_id: yup.number().required('O procedimento é obrigatório'),
      appointment_date: yup.date().required('A data de agendamento é obrigatória'),
      patient_id: yup.number().required('O paciente é obrigatório'),
    });

    try {
      await schema.validate({
        ...guide,
        payment_method_id: paymentMethod.payment_method_id,
      });
    } catch (err) {
      const error = err as yup.ValidationError;
      if (error.path && error.message) {
        setValidation({
          [error.path]: error.message,
        });
      }
      throw err;
    }
  }

  const [validation, setValidation] = useState<GuideValidation>({});
  return [validation, setValidation, handleValidation];
}
